/**
 * Utilitários de Exportação do Dashboard
 * 
 * Funções para exportar dados do dashboard em diferentes formatos:
 * - PDF: Relatório completo do dashboard
 * - Excel/CSV: Dados tabulares para análise
 * 
 * @module utils/dashboard/exportUtils
 */

import jsPDF from 'jspdf';
import moment from 'moment';
import kpiConfig from './kpiConfig';

/**
 * Formata um valor para exibição
 * @param {*} value - Valor a ser formatado
 * @param {string} unit - Unidade (%, R$, etc.)
 * @returns {string} Valor formatado
 */
const formatValue = (value, unit = '') => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'number') {
        if (unit === '%') {
            return `${value.toFixed(1)}%`;
        }
        return `${value}${unit}`;
    }
    return `${value}${unit}`;
};

/**
 * Gera um relatório PDF do dashboard
 * 
 * @param {object} data - Dados do dashboard
 * @param {object} data.summary - Resumo do dashboard
 * @param {Array} data.taskDistribution - Distribuição de tarefas
 * @param {Array} data.workload - Carga de trabalho por profissional
 * @param {Array} data.equipmentStatus - Status dos equipamentos
 * @param {Array} data.pontoDesvios - Desvios de ponto
 * @param {Array} data.estoqueAlertas - Alertas de estoque
 * @param {object} options - Opções de exportação
 * @param {string} [options.period] - Período selecionado
 * @param {Array} [options.visibleKPIs] - KPIs visíveis
 * @param {string} [options.organizationName] - Nome da organização
 * 
 * @returns {Promise<void>} Promise que resolve quando o PDF é gerado
 */
export const exportDashboardToPDF = async (data, options = {}) => {
    const {
        summary = null,
        taskDistribution = [],
        workload = [],
        equipmentStatus = [],
        pontoDesvios = [],
        estoqueAlertas = [],
    } = data;

    const {
        period = '30',
        visibleKPIs = null,
        organizationName = 'Organização',
    } = options;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const maxWidth = pageWidth - (margin * 2);
    let yPos = margin;

    // Função auxiliar para adicionar nova página se necessário
    const checkNewPage = (requiredHeight = 20) => {
        if (yPos + requiredHeight > pageHeight - margin) {
            doc.addPage();
            yPos = margin;
            return true;
        }
        return false;
    };

    // Função auxiliar para adicionar título de seção
    const addSectionTitle = (title, fontSize = 14) => {
        checkNewPage(15);
        yPos += 5;
        doc.setFontSize(fontSize);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(16, 185, 129); // emerald-600
        doc.text(title, margin, yPos);
        yPos += 8;
        doc.setFont(undefined, 'normal');
        doc.setTextColor(0, 0, 0);
    };

    // ============================================
    // CAPA / CABEÇALHO
    // ===========================================
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(16, 185, 129); // emerald-600
    doc.text('Dashboard de Gestão', margin, yPos);
    yPos += 8;

    doc.setFontSize(12);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(organizationName, margin, yPos);
    yPos += 6;

    const dataAtual = moment().format('DD/MM/YYYY');
    const horaAtual = moment().format('HH:mm');
    doc.setFontSize(10);
    doc.text(`Gerado em: ${dataAtual} às ${horaAtual}`, margin, yPos);
    yPos += 6;

    const periodLabels = {
        '7': 'Últimos 7 dias',
        '30': 'Últimos 30 dias',
        '90': 'Últimos 90 dias',
        '365': 'Último ano',
    };
    doc.text(`Período: ${periodLabels[period] || 'Últimos 30 dias'}`, margin, yPos);
    yPos += 15;

    // Linha divisória
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 10;

    // ============================================
    // RESUMO DE KPIs
    // ===========================================
    if (summary) {
        addSectionTitle('Resumo de Métricas (KPIs)', 14);

        // Filtrar KPIs visíveis se especificado
        const kpisToShow = visibleKPIs
            ? kpiConfig.filter(kpi => visibleKPIs.includes(kpi.key))
            : kpiConfig;

        const mainKPIs = kpisToShow.filter(kpi => kpi.category === 'main');
        const alertKPIs = kpisToShow.filter(kpi => kpi.category === 'alert');

        // KPIs Principais
        if (mainKPIs.length > 0) {
            doc.setFontSize(11);
            doc.setFont(undefined, 'bold');
            doc.text('Métricas Principais', margin, yPos);
            yPos += 7;

            doc.setFontSize(9);
            doc.setFont(undefined, 'normal');
            
            const colsPerRow = 2;
            const colWidth = (maxWidth - 10) / colsPerRow;
            let currentCol = 0;

            mainKPIs.forEach((kpi, index) => {
                if (currentCol === 0) {
                    checkNewPage(15);
                }

                const xPos = margin + (currentCol * (colWidth + 5));
                const value = summary[kpi.dataKey];
                
                doc.setFont(undefined, 'bold');
                doc.text(`${kpi.label}:`, xPos, yPos);
                doc.setFont(undefined, 'normal');
                doc.text(formatValue(value, kpi.unit), xPos + 50, yPos);

                currentCol++;
                if (currentCol >= colsPerRow) {
                    currentCol = 0;
                    yPos += 7;
                }
            });

            if (currentCol > 0) {
                yPos += 7;
            }
            yPos += 5;
        }

        // KPIs de Alertas
        if (alertKPIs.length > 0) {
            checkNewPage(15);
            doc.setFontSize(11);
            doc.setFont(undefined, 'bold');
            doc.text('Alertas', margin, yPos);
            yPos += 7;

            doc.setFontSize(9);
            doc.setFont(undefined, 'normal');

            alertKPIs.forEach((kpi) => {
                checkNewPage(10);
                const value = summary[kpi.dataKey];
                doc.setFont(undefined, 'bold');
                doc.text(`${kpi.label}:`, margin, yPos);
                doc.setFont(undefined, 'normal');
                doc.text(formatValue(value, kpi.unit), margin + 60, yPos);
                yPos += 7;
            });
            yPos += 5;
        }
    }

    // ============================================
    // DISTRIBUIÇÃO DE TAREFAS
    // ===========================================
    if (taskDistribution && taskDistribution.length > 0) {
        addSectionTitle('Distribuição de Tarefas', 14);

        checkNewPage(20);
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        
        // Cabeçalho da tabela
        doc.setFillColor(240, 240, 240);
        doc.rect(margin, yPos - 5, maxWidth, 8, 'F');
        doc.text('Tipo de Tarefa', margin + 2, yPos);
        doc.text('Quantidade', margin + 100, yPos);
        yPos += 8;

        // Dados
        doc.setFont(undefined, 'normal');
        taskDistribution.forEach((item) => {
            checkNewPage(10);
            const tipoTarefa = item.tipo_tarefa || item.name || 'N/A';
            const total = item.total || item.value || 0;
            doc.text(tipoTarefa, margin + 2, yPos);
            doc.text(total.toString(), margin + 100, yPos);
            yPos += 7;
        });
        yPos += 5;
    }

    // ============================================
    // CARGA DE TRABALHO POR PROFISSIONAL
    // ===========================================
    if (workload && workload.length > 0) {
        addSectionTitle('Carga de Trabalho por Profissional', 14);

        checkNewPage(20);
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        
        // Cabeçalho da tabela
        doc.setFillColor(240, 240, 240);
        doc.rect(margin, yPos - 5, maxWidth, 8, 'F');
        doc.text('Profissional', margin + 2, yPos);
        doc.text('Total de Tarefas', margin + 100, yPos);
        yPos += 8;

        // Dados
        doc.setFont(undefined, 'normal');
        workload.forEach((item) => {
            checkNewPage(10);
            const nome = item.profissional_nome || item.name || 'N/A';
            const total = item.total_tarefas || item.value || 0;
            doc.text(nome, margin + 2, yPos);
            doc.text(total.toString(), margin + 100, yPos);
            yPos += 7;
        });
        yPos += 5;
    }

    // ============================================
    // STATUS DOS EQUIPAMENTOS
    // ===========================================
    if (equipmentStatus && equipmentStatus.length > 0) {
        addSectionTitle('Status dos Equipamentos', 14);

        checkNewPage(20);
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        
        // Cabeçalho da tabela
        doc.setFillColor(240, 240, 240);
        doc.rect(margin, yPos - 5, maxWidth, 8, 'F');
        doc.text('Status', margin + 2, yPos);
        doc.text('Quantidade', margin + 100, yPos);
        yPos += 8;

        // Dados
        doc.setFont(undefined, 'normal');
        equipmentStatus.forEach((item) => {
            checkNewPage(10);
            const status = item.status || item.name || 'N/A';
            const quantidade = item.quantidade || item.total || item.value || 0;
            doc.text(status, margin + 2, yPos);
            doc.text(quantidade.toString(), margin + 100, yPos);
            yPos += 7;
        });
        yPos += 5;
    }

    // ============================================
    // DESVIOS DE PONTO
    // ===========================================
    if (pontoDesvios && pontoDesvios.length > 0) {
        addSectionTitle('Desvios de Ponto Recentes', 14);

        checkNewPage(25);
        doc.setFontSize(8);
        doc.setFont(undefined, 'bold');
        
        // Cabeçalho da tabela
        doc.setFillColor(240, 240, 240);
        doc.rect(margin, yPos - 5, maxWidth, 8, 'F');
        doc.text('Profissional', margin + 2, yPos);
        doc.text('Previsto', margin + 50, yPos);
        doc.text('Check-in', margin + 90, yPos);
        doc.text('Atraso', margin + 130, yPos);
        yPos += 8;

        // Dados
        doc.setFont(undefined, 'normal');
        pontoDesvios.slice(0, 10).forEach((desvio) => { // Limitar a 10 itens
            checkNewPage(10);
            const profissional = desvio.profissional_nome || 'N/A';
            const previsto = desvio.agendamento_previsto 
                ? moment(desvio.agendamento_previsto).format('DD/MM HH:mm')
                : 'N/A';
            const checkin = desvio.checkin_realizado
                ? moment(desvio.checkin_realizado).format('DD/MM HH:mm')
                : 'N/A';
            const atraso = desvio.minutos_atraso ? `${desvio.minutos_atraso} min` : 'N/A';
            
            doc.text(profissional.substring(0, 20), margin + 2, yPos);
            doc.text(previsto, margin + 50, yPos);
            doc.text(checkin, margin + 90, yPos);
            doc.text(atraso, margin + 130, yPos);
            yPos += 7;
        });
        yPos += 5;
    }

    // ============================================
    // ALERTAS DE ESTOQUE
    // ===========================================
    if (estoqueAlertas && estoqueAlertas.length > 0) {
        addSectionTitle('Alertas de Estoque e Validade', 14);

        checkNewPage(25);
        doc.setFontSize(8);
        doc.setFont(undefined, 'bold');
        
        // Cabeçalho da tabela
        doc.setFillColor(240, 240, 240);
        doc.rect(margin, yPos - 5, maxWidth, 8, 'F');
        doc.text('Alerta', margin + 2, yPos);
        doc.text('Medicamento', margin + 40, yPos);
        doc.text('Qtd.', margin + 100, yPos);
        doc.text('Vencimento', margin + 130, yPos);
        yPos += 8;

        // Dados
        doc.setFont(undefined, 'normal');
        estoqueAlertas.slice(0, 10).forEach((alerta) => { // Limitar a 10 itens
            checkNewPage(10);
            const tipoAlerta = alerta.tipo_alerta === 'ESTOQUE_BAIXO' ? 'Estoque Baixo' : 'Validade Próxima';
            const medicamento = alerta.medicamento_nome || 'N/A';
            const quantidade = alerta.quantidade_disponivel || 0;
            const validade = alerta.data_validade
                ? moment(alerta.data_validade).format('DD/MM/YYYY')
                : 'N/A';
            
            doc.text(tipoAlerta.substring(0, 15), margin + 2, yPos);
            doc.text(medicamento.substring(0, 25), margin + 40, yPos);
            doc.text(`${quantidade} ${alerta.unidade_medida || ''}`, margin + 100, yPos);
            doc.text(validade, margin + 130, yPos);
            yPos += 7;
        });
    }

    // ============================================
    // RODAPÉ
    // ===========================================
    const totalPages = doc.internal.pages.length - 1;
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(
            `Página ${i} de ${totalPages}`,
            pageWidth - margin - 30,
            pageHeight - 10
        );
    }

    // Salvar PDF
    const fileName = `dashboard-gestao-${moment().format('YYYY-MM-DD-HHmm')}.pdf`;
    doc.save(fileName);
};

/**
 * Converte dados para formato CSV
 * 
 * @param {Array} data - Array de objetos
 * @param {Array} columns - Array de objetos com { key, label }
 * @returns {string} String CSV
 */
const convertToCSV = (data, columns) => {
    if (!data || data.length === 0) return '';

    // Cabeçalho
    const header = columns.map(col => col.label).join(',');
    
    // Linhas de dados
    const rows = data.map(item => {
        return columns.map(col => {
            const value = item[col.key];
            // Escapar vírgulas e aspas
            if (value === null || value === undefined) return '';
            const stringValue = String(value);
            if (stringValue.includes(',') || stringValue.includes('"')) {
                return `"${stringValue.replace(/"/g, '""')}"`;
            }
            return stringValue;
        }).join(',');
    });

    return [header, ...rows].join('\n');
};

/**
 * Gera um arquivo Excel/CSV do dashboard
 * 
 * @param {object} data - Dados do dashboard
 * @param {object} options - Opções de exportação
 * @param {string} [options.period] - Período selecionado
 * @param {string} [options.organizationName] - Nome da organização
 * 
 * @returns {Promise<void>} Promise que resolve quando o arquivo é gerado
 */
export const exportDashboardToCSV = async (data, options = {}) => {
    const {
        summary = null,
        taskDistribution = [],
        workload = [],
        equipmentStatus = [],
        pontoDesvios = [],
        estoqueAlertas = [],
    } = data;

    const {
        period = '30',
        organizationName = 'Organização',
    } = options;

    const csvParts = [];

    // Cabeçalho do arquivo
    csvParts.push(`Dashboard de Gestão - ${organizationName}`);
    csvParts.push(`Gerado em: ${moment().format('DD/MM/YYYY HH:mm')}`);
    const periodLabels = {
        '7': 'Últimos 7 dias',
        '30': 'Últimos 30 dias',
        '90': 'Últimos 90 dias',
        '365': 'Último ano',
    };
    csvParts.push(`Período: ${periodLabels[period] || 'Últimos 30 dias'}`);
    csvParts.push('');

    // Resumo de KPIs
    if (summary) {
        csvParts.push('=== RESUMO DE MÉTRICAS (KPIs) ===');
        const kpiRows = [];
        kpiConfig.forEach((kpi) => {
            const value = summary[kpi.dataKey];
            kpiRows.push({
                'Métrica': kpi.label,
                'Valor': formatValue(value, kpi.unit),
            });
        });
        csvParts.push(convertToCSV(kpiRows, [
            { key: 'Métrica', label: 'Métrica' },
            { key: 'Valor', label: 'Valor' },
        ]));
        csvParts.push('');
    }

    // Distribuição de Tarefas
    if (taskDistribution && taskDistribution.length > 0) {
        csvParts.push('=== DISTRIBUIÇÃO DE TAREFAS ===');
        const taskData = taskDistribution.map(item => ({
            tipo_tarefa: item.tipo_tarefa || item.name || 'N/A',
            total: item.total || item.value || 0,
        }));
        csvParts.push(convertToCSV(taskData, [
            { key: 'tipo_tarefa', label: 'Tipo de Tarefa' },
            { key: 'total', label: 'Quantidade' },
        ]));
        csvParts.push('');
    }

    // Carga de Trabalho
    if (workload && workload.length > 0) {
        csvParts.push('=== CARGA DE TRABALHO POR PROFISSIONAL ===');
        const workloadData = workload.map(item => ({
            profissional_nome: item.profissional_nome || item.name || 'N/A',
            total_tarefas: item.total_tarefas || item.value || 0,
        }));
        csvParts.push(convertToCSV(workloadData, [
            { key: 'profissional_nome', label: 'Profissional' },
            { key: 'total_tarefas', label: 'Total de Tarefas' },
        ]));
        csvParts.push('');
    }

    // Status dos Equipamentos
    if (equipmentStatus && equipmentStatus.length > 0) {
        csvParts.push('=== STATUS DOS EQUIPAMENTOS ===');
        const equipmentData = equipmentStatus.map(item => ({
            status: item.status || item.name || 'N/A',
            quantidade: item.quantidade || item.total || item.value || 0,
        }));
        csvParts.push(convertToCSV(equipmentData, [
            { key: 'status', label: 'Status' },
            { key: 'quantidade', label: 'Quantidade' },
        ]));
        csvParts.push('');
    }

    // Desvios de Ponto
    if (pontoDesvios && pontoDesvios.length > 0) {
        csvParts.push('=== DESVIOS DE PONTO RECENTES ===');
        const pontoData = pontoDesvios.map(item => ({
            profissional_nome: item.profissional_nome || 'N/A',
            paciente_nome: item.paciente_nome || 'N/A',
            agendamento_previsto: item.agendamento_previsto 
                ? moment(item.agendamento_previsto).format('DD/MM/YYYY HH:mm')
                : 'N/A',
            checkin_realizado: item.checkin_realizado
                ? moment(item.checkin_realizado).format('DD/MM/YYYY HH:mm')
                : 'N/A',
            minutos_atraso: item.minutos_atraso || 0,
        }));
        csvParts.push(convertToCSV(pontoData, [
            { key: 'profissional_nome', label: 'Profissional' },
            { key: 'paciente_nome', label: 'Paciente' },
            { key: 'agendamento_previsto', label: 'Previsto' },
            { key: 'checkin_realizado', label: 'Check-in' },
            { key: 'minutos_atraso', label: 'Atraso (min)' },
        ]));
        csvParts.push('');
    }

    // Alertas de Estoque
    if (estoqueAlertas && estoqueAlertas.length > 0) {
        csvParts.push('=== ALERTAS DE ESTOQUE E VALIDADE ===');
        const estoqueData = estoqueAlertas.map(item => ({
            tipo_alerta: item.tipo_alerta === 'ESTOQUE_BAIXO' ? 'Estoque Baixo' : 'Validade Próxima',
            medicamento_nome: item.medicamento_nome || 'N/A',
            numero_lote: item.numero_lote || 'N/A',
            quantidade_disponivel: item.quantidade_disponivel || 0,
            unidade_medida: item.unidade_medida || '',
            data_validade: item.data_validade
                ? moment(item.data_validade).format('DD/MM/YYYY')
                : 'N/A',
            dias_para_vencer: item.dias_para_vencer || 0,
        }));
        csvParts.push(convertToCSV(estoqueData, [
            { key: 'tipo_alerta', label: 'Tipo de Alerta' },
            { key: 'medicamento_nome', label: 'Medicamento' },
            { key: 'numero_lote', label: 'Lote' },
            { key: 'quantidade_disponivel', label: 'Quantidade Disponível' },
            { key: 'unidade_medida', label: 'Unidade' },
            { key: 'data_validade', label: 'Data de Validade' },
            { key: 'dias_para_vencer', label: 'Dias para Vencer' },
        ]));
    }

    // Criar blob e fazer download
    const csvContent = csvParts.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `dashboard-gestao-${moment().format('YYYY-MM-DD-HHmm')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

/**
 * Gera um relatório completo do dashboard
 * 
 * @param {object} data - Dados do dashboard
 * @param {string} format - Formato de exportação ('pdf' ou 'csv')
 * @param {object} options - Opções de exportação
 * 
 * @returns {Promise<void>} Promise que resolve quando o relatório é gerado
 */
export const generateDashboardReport = async (data, format = 'pdf', options = {}) => {
    try {
        if (format === 'pdf') {
            await exportDashboardToPDF(data, options);
        } else if (format === 'csv') {
            await exportDashboardToCSV(data, options);
        } else {
            throw new Error(`Formato não suportado: ${format}`);
        }
    } catch (error) {
        console.error('Erro ao gerar relatório:', error);
        throw error;
    }
};

export default {
    exportDashboardToPDF,
    exportDashboardToCSV,
    generateDashboardReport,
};

